import {
  DEVICE_ID_HEADER,
  type AssigneeWorkload,
  type ChangePasswordBody,
  type ChangePasswordResponse,
  type ConversationSummary,
  type DirectMessagePayload,
  type MessageableUser,
  type NotificationFeed,
  type PushSubscribeBody,
  type SessionSummary,
  type CreateMessageBody,
  type CreateTicketBody,
  type ListTicketsQuery,
  type TicketDetail,
  type TicketMessagePayload,
  type TicketSummary,
  type TransitionTicketBody,
  type UpdateChecklistStepBody,
} from '@nbh/shared';
import { getDeviceId } from './device';

/**
 * A response the server actually sent, as opposed to one that never happened.
 *
 * The distinction is not cosmetic. `fetch` rejects with a bare `TypeError` when there is
 * no network, and until this existed that was indistinguishable from a 401 — so an
 * offline cold start looked exactly like being signed out, and every authenticated
 * screen bounced to the login form with a full IndexedDB sitting underneath it.
 * Carrying the status is what lets a caller tell "you may not" from "not right now".
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Seconds to wait, when the server sent a 429 saying so. Read from the body rather
     * than the `Retry-After` header because this client reads bodies and the header is
     * not exposed to script on a cross-origin response; the server sends both.
     */
    readonly retryAfterSeconds?: number,
    /**
     * Which policy rules a rejected password broke (D3).
     *
     * Codes, not sentences: the server refuses in one place and the client renders its
     * own i18n copy, so no user-facing text is composed on the server. Present only on a
     * 400 from a password route.
     */
    readonly violations?: string[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Typed API client. Session travels in the httpOnly cookie, so nothing here handles
 * tokens — `credentials: 'include'` is all that is needed, and it keeps the token
 * out of reach of any script on the page.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Multipart must be left alone: the boundary is generated with the body and only the
  // browser knows it, so declaring a Content-Type here produces a request no server can
  // parse. Every other body this client sends is JSON.
  const isMultipart = init?.body instanceof FormData;

  // The device id goes on every request, not just writes: any successful call is
  // evidence this device was online, which is what bounds backdating later (D18).
  const headers: Record<string, string> = {
    [DEVICE_ID_HEADER]: getDeviceId(),
    ...(init?.body && !isMultipart ? { 'Content-Type': 'application/json' } : {}),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };

  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      message?: unknown;
      retryAfterSeconds?: number;
      violations?: string[];
    } | null;
    // Nest's default exception body carries the rule in `message` and only the status
    // name in `error` — which is how a technician refused a job was shown the word
    // "Forbidden" and nothing else. The routes that build their own body (validation,
    // throttling, password policy) put their copy in `error` and send no `message`,
    // so preferring a string `message` reads the named rule where there is one and
    // changes nothing where there is not.
    throw new ApiError(
      typeof body?.message === 'string'
        ? body.message
        : (body?.error ?? `Request failed (${response.status})`),
      response.status,
      body?.retryAfterSeconds,
      body?.violations,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

function toQueryString(query: Partial<ListTicketsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}

/**
 * The detail payload, defined once in `@nbh/shared` and re-exported under the name the
 * client already uses.
 *
 * It was previously declared here by hand, and the hand-written version was wrong: the
 * endpoint returned the raw Prisma row, which carries `assignee` but no `assigneeName`,
 * so the field the detail screen reads was always undefined. Taking the type from the
 * contract package means the server and the screen can no longer disagree in silence.
 */
export type TicketDetailResponse = TicketDetail;

/** One device enrolled for push. Dates arrive as ISO strings over the wire. */
export interface PushDevice {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastNotifiedAt: string | null;
}

export interface SessionUser {
  id: string;
  email: string;
  role: string;
  department: string | null;
  /**
   * Whether somebody else set this password and its owner has not chosen their own yet.
   *
   * Drives the third routing state (D8). Optional because a session cached from before
   * this field existed will not carry it, and a missing flag must read as "no" rather
   * than trapping somebody on the change screen with a stale cache.
   */
  mustChangePassword?: boolean;
}

export interface LocationOption {
  id: string;
  name: string;
  floor: string | null;
  qrCode: string | null;
  /**
   * Null for a top-level place, and also — importantly — for a place whose parent is
   * simply not in this response: `?qrCode=` returns one row. The picker nests on this
   * and must treat an unresolvable parent as top-level, never drop the row.
   */
  parentId: string | null;
}

export const api = {
  /** Resolves the current session, or throws 401 when signed out. */
  me: () => request<SessionUser>('/auth/me'),

  login: (email: string, password: string) =>
    request<SessionUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /**
   * Changes your own password. The response carries no credential — deliberately.
   */
  changePassword: (body: ChangePasswordBody) =>
    request<ChangePasswordResponse>('/auth/password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listLocations: (qrCode?: string) =>
    request<LocationOption[]>(`/locations${qrCode ? `?qrCode=${encodeURIComponent(qrCode)}` : ''}`),

  /**
   * Direct messages (D29). Distinct from `getTicket().messages`, which is a thread
   * about a job; these belong to two people and to no job.
   */
  messageDirectory: () => request<MessageableUser[]>('/messages/directory'),

  listConversations: () => request<ConversationSummary[]>('/messages/conversations'),

  /** Idempotent — returns the existing thread with this person if there is one. */
  startConversation: (userId: string) =>
    request<{ id: string }>('/messages/conversations', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  listDirectMessages: (conversationId: string) =>
    request<DirectMessagePayload[]>(`/messages/conversations/${conversationId}`),

  sendDirectMessage: (conversationId: string, body: string) =>
    request<DirectMessagePayload>(`/messages/conversations/${conversationId}`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  /** The bell: drawer contents and badge count in one call (D30). */
  listNotifications: () => request<NotificationFeed>('/notifications'),

  markNotificationsRead: () =>
    request<{ unread: number }>('/notifications/read', { method: 'POST' }),

  /**
   * Web Push enrolment (D14, D39).
   *
   * Routed through `request` like every other call rather than the bare `fetch` these
   * used to be, so they carry the device id (D18) and the session cookie, and so a
   * caller can tell a refusal from no network at all. The public key endpoint is
   * `@Public()` on the server and needs neither, but sending them costs nothing and
   * keeps one way of talking to the API.
   */
  pushPublicKey: () => request<{ publicKey: string | null }>('/push/public-key'),

  /** Devices enrolled for alerts. Endpoint has existed since Phase 4 with no caller. */
  listPushDevices: () => request<PushDevice[]>('/push/devices'),

  /**
   * Turns alerts off on *every* device at once.
   *
   * The server offers no per-device delete — `DELETE /push/devices` removes them all —
   * so the screen says so rather than implying a choice it cannot honour.
   */
  forgetAllPushDevices: () => request<void>('/push/devices', { method: 'DELETE' }),

  /** Signed-in devices, and ending one (D41). */
  listSessions: () => request<SessionSummary[]>('/auth/sessions'),

  revokeSession: (id: string) => request<void>(`/auth/sessions/${id}`, { method: 'DELETE' }),

  subscribePush: (body: PushSubscribeBody) =>
    request<{ id: string }>('/push/subscribe', { method: 'POST', body: JSON.stringify(body) }),

  unsubscribePush: (endpoint: string) =>
    request<void>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  /**
   * A test alert to the calling account's own devices, and only those. What makes the
   * settings switch believable: "I never got an alert" is settled in five seconds
   * without anyone reading a log.
   */
  sendTestPush: () =>
    request<{ sent: number; failed: number; pruned: number }>('/push/test', { method: 'POST' }),

  /**
   * Uploads one file and returns the orphaned attachment it created.
   *
   * Media goes up on its own and is claimed afterwards by whatever references it — a
   * report, a transition, a checklist step — so a slow photo never blocks the write it
   * belongs to. `form` is built by the caller because only the caller knows the filename.
   *
   * Routed through `request` like everything else so it carries the device id (D18) and
   * the session cookie, and so a caller can tell 413 from 400 from no network at all.
   * It used to be a bare `fetch` with none of that, throwing a plain `Error` whose only
   * content was a status code baked into an untranslatable string.
   */
  uploadAttachment: (form: FormData) =>
    request<{ id: string; url: string }>('/attachments', { method: 'POST', body: form }),

  listTickets: (query: Partial<ListTicketsQuery> = {}) =>
    request<TicketSummary[]>(`/tickets${toQueryString(query)}`),

  getTicket: (id: string) => request<TicketDetailResponse>(`/tickets/${id}`),

  createTicket: (body: CreateTicketBody) =>
    request<{ id: string; reference: number }>('/tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listMessages: (ticketId: string) =>
    request<TicketMessagePayload[]>(`/tickets/${ticketId}/messages`),

  postMessage: (ticketId: string, body: CreateMessageBody) =>
    request<TicketMessagePayload>(`/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Walk the state machine. ON_HOLD has its own endpoints — it moves the SLA clock. */
  transitionTicket: (
    ticketId: string,
    to: TransitionTicketBody['to'],
    reason?: string,
    proofAttachmentId?: string,
  ) =>
    request<{ id: string; status: string }>(`/tickets/${ticketId}/transition`, {
      method: 'POST',
      body: JSON.stringify({ to, reason, proofAttachmentId }),
    }),

  /**
   * Move the job to somebody (D38).
   *
   * The same call for a dispatch and for taking a job off one technician and giving it to
   * another — it is one write, and which of the two it turns out to be is a property of
   * the row rather than of this request. The reason is optional and stays optional.
   */
  assignTicket: (ticketId: string, assigneeId: string, reason?: string) =>
    request<{ id: string; assigneeId: string | null }>(`/tickets/${ticketId}/assignee`, {
      method: 'POST',
      body: JSON.stringify({ assigneeId, reason }),
    }),

  /** Live job counts per person, so the picker can show who is already loaded. */
  ticketWorkload: () => request<AssigneeWorkload[]>('/tickets/workload'),

  updateChecklistStep: (ticketId: string, stepId: string, body: UpdateChecklistStepBody) =>
    request<unknown>(`/tickets/${ticketId}/checklist/${stepId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  holdTicket: (ticketId: string, reason: string) =>
    request<unknown>(`/tickets/${ticketId}/hold`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  resumeTicket: (ticketId: string) =>
    request<unknown>(`/tickets/${ticketId}/resume`, { method: 'POST' }),
};
